import { uniqBy } from "lodash";
import { localizeNumber } from "./Functions";

/**
 * adding <NUM_TABS> copies of a page
 * instance object contains info specific to that page
 * index refers to the active tab index for that page
 * exportComparisonView (for first tab pages) determines which view to show
 */
export function flatProductPages(pages, exportState) {
  const NUM_TABS = 4;
  const flatPages = [];
  const showBothComparisons =
    exportState.brandModels?.length > 0 &&
    exportState.competitorModels?.length > 0;
  for (const page of pages) {
    for (let i = 0; i < NUM_TABS; i++) {
      const instance = {
        index: i
      };
      //exportPageNum is for the query param, easier for the client than starting at 0
      //if showing both, make the first tab 0 page be competitor models
      if (i === 0) {
        instance.exportPageNum = 1;
        if (!!showBothComparisons) instance.exportComparisonView = "competitor";
        else
          instance.exportComparisonView =
            exportState.brandModels?.length > 0 ? "brand" : "competitor";
      } else instance.exportPageNum = !!showBothComparisons ? i + 2 : i + 1;
      flatPages.push({ ...page, instance });
      //if both should be shown, add another tab 0 page this time with brand
      if (i === 0 && !!showBothComparisons) {
        flatPages.push({
          ...page,
          instance: {
            exportPageNum: 2,
            index: 0,
            exportComparisonView: "brand"
          }
        });
      }
    }
  }
  return flatPages;
}

//formats attribute choices for dropdown for find by features
export function getAttrChoicesForDropdown(attribute, products) {
  const { input_type } = attribute;
  //some values come from products themselves so get that attr from each product
  const productsAttributes = products.reduce((filtered, p) => {
    const productAttrs = p.attributes.filter(
      ({ specification }) => specification.id === attribute.id
    );
    if (productAttrs.length > 0) filtered.push(productAttrs);
    return filtered;
  }, []);
  switch (input_type) {
    case "text_input": {
      const values = productsAttributes.map((pas) => {
        const displayValue = getAttrDisplayValue(pas);
        return {
          label: displayValue,
          value: displayValue
        };
      });
      return uniqBy(values, "value");
    }
    case "numeric": {
      const values = productsAttributes
        .map((pas) => {
          const displayValue = getAttrDisplayValue(pas);
          return {
            unit: pas[0]?.unit,
            label: displayValue,
            value: displayValue
          };
        })
        .sort((a, b) => a.unit.localeCompare(b.unit));
      return uniqBy(values, "value");
    }
    default: {
      // get all the choices being used by products and put them in a set
      const productChoices = productsAttributes.reduce((filtered, pas) => {
        const displayValue = splitAttrChoices(pas[0]);
        filtered = filtered.concat(displayValue);
        return filtered;
      }, []);
      const productChoicesSet = new Set(productChoices);
      //return the choices from the specification (to get correct order) that products have
      return attribute.choices.reduce((filtered, choice) => {
        if (productChoicesSet.has(choice.value))
          filtered.push({
            label: choice.label,
            value: choice.value
          });
        return filtered;
      }, []);
    }
  }
}

//if the facet exists, it shouldn't be hidden, and shouldn't be suppressed,
//put it in the right position
function getFacetedValue(str, facet, specification, dp) {
  const { facet_placement, facet_separator } = specification;
  const fp = facet_placement || "after";
  const fs = facet_separator || "";
  const showFacet = !!facet && fp !== "hidden";
  if (!showFacet) return str;
  const f = getSpecPropLabel(facet, "facets", specification, dp);
  return fp === "before" ? `${f}${fs}${str}` : `${str}${fs}${f}`;
}

//prop="choices","units",or "facets"
export function getAssociatedDisclaimer(value, prop, specification) {
  //GET LABEL FIRST
  //label doesn't always exist so specPropLabel might return value still
  //so check equality against both to ensure getting the disclaimer
  return specification[prop].find((c) => c.value === value || c.label === value)
    ?.associated_disclaimer;
}

//prop="choices","units",or "facets"
export function getSpecPropLabel(value, prop, specification, dp) {
  //if dp supplied, it'll process the associated disclaimer
  const foundProp = specification[prop].find((c) => c.value === value);
  if (!foundProp) return dpps(dp, value);
  const label = foundProp.label || foundProp.value;
  return dpps(dp, label, foundProp.associated_disclaimer);
}

//shorthand disclaimer processing
function dpps(dp, str, d) {
  return dp ? dp.ps(str, d) : str;
}

//sort dimension facets by the custom order given (array)
export function sortFacets(attributes, facetsOrder) {
  const order = {};
  for (let i = 0; i < facetsOrder.length; i++) order[facetsOrder[i]] = i;
  attributes.sort((a, b) => order[a.facet] - order[b.facet]);
}

function formatUnit(unit, specification, dp, onlyShowLast, isLast) {
  if (onlyShowLast && !isLast) return "";
  else {
    const { unit_separator } = specification;
    const u = getSpecPropLabel(unit, "units", specification, dp);
    return u ? `${unit_separator}${u}` : "";
  }
}

function formatChoice(args) {
  const {
    choice,
    specification,
    dp,
    idx,
    length,
    unit,
    only_show_last_unit,
    value_prefix,
    facet
  } = args;
  const choice_label = getSpecPropLabel(choice, "choices", specification, dp);
  const isLast = idx === length - 1;
  const formatted_unit = formatUnit(
    unit,
    specification,
    dp,
    only_show_last_unit,
    isLast
  );
  const value = `${value_prefix}${choice_label}${formatted_unit}`;
  const facetedValue = getFacetedValue(value, facet, specification, dp);
  return facetedValue.trim();
}

function getAttrDisplayValue_checkboxes(productAttributes, dp) {
  const { specification } = productAttributes[0];
  const {
    only_show_last_unit,
    value_prefix = "",
    value_separator = ""
  } = specification;
  return productAttributes
    .map((productAttr) => {
      const { facet, unit } = productAttr;
      const value_override = getOverrideValue(productAttr);
      if (value_override) return value_override.trim();
      const choices = splitAttrChoices(productAttr);
      const choiceSet = new Set(choices);
      //get them in the order specified in the specification
      let sortedChoices = specification?.choices
        ?.filter((c) => choiceSet.has(c.value))
        ?.map((c) => c.label);
      if (sortedChoices.length === 0) sortedChoices = choices;
      const formattedChoices = sortedChoices.map((choice, c_idx) => {
        return formatChoice({
          choice,
          specification,
          dp,
          idx: c_idx,
          length: productAttributes.length,
          unit,
          only_show_last_unit,
          value_prefix,
          facet
        });
      });
      return formattedChoices.join(value_separator);
    })
    .join(value_separator);
}

function getAttrDisplayValue_dropdown(productAttributes, dp) {
  const { specification } = productAttributes[0];
  const {
    only_show_last_unit,
    value_prefix = "",
    value_separator = ""
  } = specification;
  return productAttributes
    .map((productAttr, pa_idx) => {
      const { choice, unit, facet } = productAttr;
      const value_override = getOverrideValue(productAttr);
      if (value_override) return value_override.trim();
      return formatChoice({
        choice,
        specification,
        dp,
        idx: pa_idx,
        length: productAttributes.length,
        unit,
        only_show_last_unit,
        value_prefix,
        facet
      });
    })
    .join(value_separator);
}

function getAttrDisplayValue_numeric(productAttributes, dp) {
  const { specification } = productAttributes[0];
  const {
    insert_thousands_separator = false,
    value_prefix = "",
    value_separator = "",
    only_show_last_unit
  } = specification;
  return productAttributes
    .map((productAttr, pa_idx) => {
      const { numeric_value, unit, facet } = productAttr;
      const value_override = getOverrideValue(productAttr);
      if (value_override) return value_override.trim();
      const localizedNumericValue = insert_thousands_separator
        ? localizeNumber(numeric_value, "en-US")
        : numeric_value;
      const isLast = pa_idx === productAttributes.length - 1;
      const formatted_unit = formatUnit(
        unit,
        specification,
        dp,
        only_show_last_unit,
        isLast
      );
      const value = `${value_prefix}${localizedNumericValue}${formatted_unit}`;
      const facetedValue = getFacetedValue(value, facet, specification, dp);
      return facetedValue.trim();
    })
    .join(value_separator);
}

function getAttrDisplayValue_default(productAttributes, dp) {
  const { specification } = productAttributes[0];
  const {
    value_prefix = "",
    value_separator = "",
    only_show_last_unit
  } = specification;
  return productAttributes
    .map((productAttr, pa_idx) => {
      const { text_value, unit, facet } = productAttr;
      const value_override = getOverrideValue(productAttr);
      if (value_override) return value_override.trim();
      const isLast = pa_idx === productAttributes.length - 1;
      const formatted_unit = formatUnit(
        unit,
        specification,
        dp,
        only_show_last_unit,
        isLast
      );
      const value = `${value_prefix}${text_value}${formatted_unit}`;
      const facetedValue = getFacetedValue(value, facet, specification, dp);
      return facetedValue.trim();
    })
    .join(value_separator);
}

//get display value for attr based on input type
//easier to put into array
//function will most likely be called after finding attr within product by filtering for that id (gives list)
//or by using find (not list)
//dimensions for example will be 3 attributes that have the same spec id
//so easier to start with all in an array
//if disclaimer processor supplied, will search for associated disclaimer and process as needed
export function getAttrDisplayValue(productAttributes, dp) {
  if (!productAttributes) return null;
  if (!Array.isArray(productAttributes)) {
    productAttributes = [productAttributes];
  }
  //but most cases we only care about the first attr
  const productAttribute = productAttributes[0];
  if (!productAttribute) return null;
  const { specification } = productAttribute;
  const { input_type = "", facets = [] } = specification || {};
  const inputType = input_type;
  sortFacets(
    productAttributes,
    facets.map(({ value }) => value)
  );
  switch (inputType) {
    //original values for choices, facets, units use values from those props in specification
    //show labels instead if they exist
    case "checkboxes": {
      return getAttrDisplayValue_checkboxes(productAttributes, dp);
    }
    case "dropdown": {
      return getAttrDisplayValue_dropdown(productAttributes, dp);
    }
    case "numeric": {
      return getAttrDisplayValue_numeric(productAttributes, dp);
    }
    //text input and others
    default: {
      return getAttrDisplayValue_default(productAttributes, dp);
    }
  }
}

export function splitAttrChoices(attr) {
  if (Array.isArray(attr.choices) && attr.choices.length > 1) {
    return attr.choices;
  }

  //because they're weird as ["USB, Ethernet"] all one string in array
  const value_separator = attr?.specification?.value_separator || ",";
  if (attr.choices?.length === 1)
    return (
      attr.choices[0]?.split?.(value_separator)?.map?.((s) => s.trim()) || []
    );

  return attr.choices?.split?.(value_separator)?.map?.((s) => s.trim()) || [];
}

//used to get just the VALUE (no units, facets, etc) for displaying an attribute value for compare table
export function getAttrInnerValue(inputType, attr, dp) {
  if (!attr) return "";
  const { specification } = attr;
  switch (inputType) {
    case "dropdown": {
      return getSpecPropLabel(attr.choice, "choices", specification, dp);
    }
    case "numeric": {
      const { insert_thousands_separator } = specification;
      const { numeric_value } = attr;
      return insert_thousands_separator
        ? localizeNumber(numeric_value, "en-US")
        : numeric_value;
    }
    default:
      return attr.text_value;
  }
}
//an array of singleton arrays where single element is the attr
export function primaryValueWins(productsAttrs) {
  const primaryProduct = productsAttrs[0];
  const primaryValue = primaryProduct[0].numeric_value || 0;
  for (const productAttrs of productsAttrs) {
    const attr = productAttrs[0];
    if (attr?.numeric_value > primaryValue) return false;
  }
  return true;
}

export function rowUsesOneValueSameFacet(productsAttrs) {
  const facet = productsAttrs[0]?.[0]?.facet;
  if (!facet) return false;
  for (const productAttrs of productsAttrs) {
    if (productAttrs.length > 1) return false;
    //if attribute value is overridden, facet might be null
    //return false if facets aren't the same, and there's no override
    //because if there is an override, it should have the same facet
    if (productAttrs[0]?.facet !== facet && !productAttrs[0]?.override)
      return false;
  }
  return true;
}

// returns the override value for a given attribute
export function getOverrideValue(attribute) {
  const { override_value_with } = attribute;
  if (
    override_value_with === "nothing" ||
    override_value_with === "" ||
    !override_value_with
  ) {
    return "";
  }
  return override_value_with;
}

//if comparison ID isn't reset, and you're on the current comparison selected
export function shouldResetSelectedComparison(
  selectedComparison,
  comparisonId
) {
  return (
    !!selectedComparison[0] &&
    !!selectedComparison[1] &&
    selectedComparison[0].substring(1) === comparisonId
  );
}

//organize the sku options into rows based on description and color
export function getSkuTableRows(skuOptions, colors) {
  //first organize by making description the key and the colors will be arrays containing sku and interface info
  //also keep track of maximum number (like if only white has 2 SKUS, maxNumSkus is 2)
  const rowMap = {};
  for (const s of skuOptions) {
    const { description, interface_configuration, ac_adapter, color, sku } = s;
    // Cannot assign a cell without matching color
    if (!color) {
      continue;
    }
    const { base_color } = color;
    const isUib = interface_configuration === "Connect-It";
    const isNoAc = ac_adapter === "AC Adapter Not Included";
    if (!rowMap[description]) {
      rowMap[description] = {
        maxNumSkus: 1,
        uib: isUib,
        noAc: isNoAc,
        [base_color]: [sku]
      };
    } else {
      const { uib, noAc } = rowMap[description];
      //if any entry qualifies uib/noAc make it true
      rowMap[description].uib = uib || isUib;
      rowMap[description].noAc = noAc || isNoAc;
      rowMap[description][base_color] = (
        rowMap[description][base_color] || []
      ).concat([sku]);
      rowMap[description].maxNumSkus = Math.max(
        rowMap[description].maxNumSkus,
        rowMap[description][base_color]?.length
      );
    }
  }
  for (const v of Object.values(rowMap)) {
    for (const { color } of colors) {
      if (v[color]) v[color] = v[color].join("<br/>");
    }
  }
  return Object.entries(rowMap);
}

/**
 * converts listString to html to remove children over the limit then returns the string
 * @param {*} listString string version of a list like "<ul><li>Item</li></ul>"
 * @param {*} limit number of items allowed in the list
 * @returns a string version of the list with at most <limit> number of items
 */
export function trimList(listString, limit) {
  const template = document.createElement("template");
  template.innerHTML = listString;
  const content = template.content;
  const list = content?.querySelector("ul, ol");
  while (list?.children?.length > limit) {
    const li = list.children[list.children.length - 1];
    list.removeChild(li);
  }
  return content?.firstChild?.outerHTML;
}

// update hash and jump to element
export function updateHashAndJump(hash, offset) {
  if (!hash || hash === "#") return;
  if (!hash.startsWith("#")) hash = hash`#${hash}`;
  const element = document.querySelector(hash);
  if (!element) return;
  window.history.pushState(null, null, hash);
  //allows for offset if toolbar is shown
  element.scrollIntoView();
  var scrolledY = window.scrollY;
  if (scrolledY) {
    window.scroll(0, scrolledY - offset);
  }
}

//gets components of selectedComparison context (originally as string)
export function getSelectedComparisonComponents(selectedComparison) {
  const { href, tab, toggleView } = JSON.parse(selectedComparison);
  return {
    selectedComparisonId: href?.split("#")?.[1],
    selectedComparisonTab: Number.parseInt(tab || -1),
    selectedComparisonToggleView: toggleView
  };
}

export function getDateDisclaimer(collections) {
  return {
    id: "dateDisclaimer",
    name: "Version & Date",
    paragraph: "3",
    symbol: "{d}",
    disclaimer_is_global: true,
    body: collections?.global_settings?.version_number
  };
}

//quick way to get product name
//args example: "brand_family_model"
export function getProductName(product, args = []) {
  if (!product) return "";
  const include = new Set(args);
  let name = "";
  if (include.has("brand"))
    name = product?.brand_family?.brand?.name || "".trim();
  if (include.has("family"))
    name = `${name} ${product?.brand_family?.family || ""}`.trim();
  if (include.has("model")) name = `${name} ${product.model}`.trim();
  return name;
}
function getProductFilter(prelaunch) {
  if (!!prelaunch) return { status: { _in: ["active", "preview"] } };
  return {
    status: { _eq: "active" },
    _or: [{ launch_date: { _lte: "$NOW" } }, { launch_date: { _null: true } }]
  };
}

function getComparisonPageFilter(prelaunch) {
  if (!!prelaunch) return { primary_product: getProductFilter(prelaunch) };
  return {
    primary_product: { status: { _eq: "active" } },
    _or: [
      { primary_product: { launch_date: { _lte: "$NOW" } } },
      { primary_product: { launch_date: { _null: true } } }
    ]
  };
}

/**
 * if prelaunch, ignore launch date and show all active or preview products
 * if not prelaunch, only show active products with non-future (or null) launch dates
 * reflect this filtering in comparison pages as well
 */
export function adjustFilterForPrelaunch(
  collectionName,
  typeConfig,
  prelaunch
) {
  if (collectionName === "products")
    typeConfig.filter = {
      ...typeConfig.filter,
      ...getProductFilter(prelaunch)
    };
  else if (collectionName === "comparison_pages")
    typeConfig.filter = {
      ...typeConfig.filter,
      ...getComparisonPageFilter(prelaunch)
    };
}

export function getPrelaunchQuery(query) {
  return query.get("prelaunch")?.toLowerCase?.() === "true";
}

//valueMap={comparison:"brand", prelaunch:true}
export const updateQuery = (urlParams, valueMap) => {
  const { history, location } = urlParams;
  const params = new URLSearchParams(history.location.search);
  for (const [k, v] of Object.entries(valueMap)) {
    if (!!v) params.set(k, v);
    //if null/undefined or false, remove it
    else params.delete(k);
  }
  const search = params.toString();
  const { pathname, hash } = location;
  history.replace({ hash, pathname, search });
};

//remove properties that make an object a circular structure
export function decircleObject(object, properties) {
  const propSet = new Set(properties);
  return Object.entries(object).reduce((acc, [k, v]) => {
    if (!propSet.has(k)) acc[k] = v;
    return acc;
  }, {});
}

/**
 * if selectedPages has productPageIdx but no products are specified, default it to "all"
 * @param {*} selectedPages set of the selectedPages specified in query ("all" or set)
 * @param {*} productPageIdx idx that the selectedModel page will be
 * @param {*} productQuery the products specified in the query ("all" or set)
 * @returns "all" or set of currently selected products from query
 */
export function defaultSelectedModels(
  selectedPages,
  productPageIdx,
  productQuery
) {
  const hasProductPage =
    selectedPages === "all" || selectedPages.has(productPageIdx);
  if (hasProductPage && productQuery.size === 0) return "all";
  //products are already selected so keep as is
  return productQuery;
}
